import { ExternalLink, LockKeyhole, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatBusinessDate, formatLocateExpiration } from "@/utils/datetime";
import { formatInformationalStatus } from "@/components/InformationalStatusText";
import HistoricalLocateDetails from "@/components/HistoricalLocateDetails";
import "./HistoricalLocateCard.css";

const TEXAS811_LOOKUP_URL = "https://txgc.texas811.org/ui/dashboard";

async function lookupTicket(reference) {
  const ticket = String(reference || "").trim();
  if (!ticket) return;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(ticket);
      toast.success("Ticket number copied for Texas811 lookup");
    }
  } catch {
    // Opening the lookup must remain possible without clipboard permission.
  }
  window.open(TEXAS811_LOOKUP_URL, "_blank", "noopener");
}

/**
 * A single readable summary card, reused in the Project Record and the
 * company-wide history. No changes to ticket status or historical write rules.
 */
export default function HistoricalLocateCard({
  record,
  testId,
  canCorrect = false,
  correctionUrl,
  onCorrect,
  showProjectLink = false,
}) {
  const reference = record.ticket_not_needed
    ? "Ticket not needed"
    : record.reference_number || "No ticket number";
  const expired = record.time_status === "expired";
  const timeLabel = expired ? "Expired" : "Inactive";
  const canShowCorrection = canCorrect && record.is_historical === true;
  const requested = record.request_date ? formatBusinessDate(record.request_date) : "Not recorded";

  return (
    <article
      className="historical-locate-card"
      data-testid={testId || `historical-locate-card-${record.id}`}
    >
      <header className="historical-locate-card__header">
        <div className="historical-locate-card__identity">
          <span className="historical-locate-card__eyebrow">Locate / 811</span>
          <h3 className="historical-locate-card__number" data-testid={`historical-locate-number-${record.id}`}>
            {reference}
          </h3>
        </div>
        <span
          className={`historical-locate-card__badge${expired ? " historical-locate-card__badge--expired" : ""}`}
          data-testid={`historical-locate-status-${record.id}`}
        >
          {timeLabel}
        </span>
      </header>

      {showProjectLink && (
        <p className="historical-locate-card__project">
          {record.project_code || "Project"}{record.project_name && record.project_name !== record.project_code ? ` · ${record.project_name}` : ""}
        </p>
      )}

      <dl className="historical-locate-card__summary" data-testid={`historical-locate-dates-${record.id}`}>
        <div>
          <dt>Requested</dt>
          <dd>{requested}</dd>
        </div>
        <div>
          <dt>Ticket coverage</dt>
          <dd>{formatLocateExpiration(record)}</dd>
        </div>
        <div>
          <dt>Condition</dt>
          <dd>{formatInformationalStatus(record.status) || "Not recorded"}</dd>
        </div>
        <div>
          <dt>Record</dt>
          <dd className="historical-locate-card__locked"><LockKeyhole size={14} aria-hidden="true" /> Historical</dd>
        </div>
      </dl>

      <HistoricalLocateDetails record={record} />

      <nav className="historical-locate-card__actions" aria-label={`Actions for locate ${reference}`}>
        {!record.ticket_not_needed && Boolean(record.reference_number) && (
          <button type="button" className="historical-locate-card__action" data-testid={`historical-locate-lookup-${record.id}`} onClick={() => lookupTicket(record.reference_number)}>
            <ExternalLink size={16} aria-hidden="true" />
            <span>811 lookup</span>
          </button>
        )}
        {showProjectLink && (
          <Link className="historical-locate-card__action" to={`/projects/${record.project_id}`}>
            Open project
          </Link>
        )}
        {canShowCorrection && (correctionUrl ? (
          <Link className="historical-locate-card__action historical-locate-card__action--correction" to={correctionUrl}>
            <Pencil size={16} aria-hidden="true" />
            <span>Correct record (audited)</span>
          </Link>
        ) : (
          <button type="button" className="historical-locate-card__action historical-locate-card__action--correction" onClick={onCorrect} disabled={!onCorrect}>
            <Pencil size={16} aria-hidden="true" />
            <span>Correct record (audited)</span>
          </button>
        ))}
      </nav>
    </article>
  );
}
